import { User, UserStats } from "../types";

/** Backend /auth/me and login/register user payload (subset). */
export type BackendAuthUser = {
  id: string;
  steamId?: string;
  showClanTag?: boolean;
  hasPassword?: boolean;
  premiumUntil?: string;
  username?: string;
  email?: string;
  elo?: number;
  isAdmin?: boolean;
  isPremium?: boolean;
  createdAt?: string;
  avatar?: string;
  background?: string;
};

const defaultStats = (): UserStats => ({
  totalMatches: 0,
  wins: 0,
  losses: 0,
  winRate: 0,
  kd: 0,
  avgScore: 0,
  bestMap: "N/A",
  favoriteWeapon: "N/A",
});

/** Faceit-style level (1..10) derived from the real backend elo. */
export const LEVEL_THRESHOLDS = [501, 751, 901, 1051, 1201, 1301, 1591, 1751, 2001];

export function levelFromElo(elo: number): number {
  let level = 1;
  for (const th of LEVEL_THRESHOLDS) {
    if (elo >= th) level += 1;
  }
  return Math.min(10, level);
}

/** Human-readable rank tier derived from the real backend elo. */
export function rankFromElo(elo: number): string {
  if (elo < 900) return "Silver";
  if (elo < 1100) return "Silver Elite";
  if (elo < 1300) return "Gold Nova";
  if (elo < 1500) return "Master Guardian";
  if (elo < 1700) return "Legendary Eagle";
  if (elo < 1900) return "Supreme";
  return "Global Elite";
}

export function mapBackendUserToApp(
  u: BackendAuthUser,
  stats?: Partial<UserStats>,
): User {
  const mergedStats = { ...defaultStats(), ...stats };
  const elo = u.elo ?? 1000;
  return {
    id: u.id,
    steamId: u.steamId,
    showClanTag: u.showClanTag,
    hasPassword: u.hasPassword,
    premiumUntil: u.premiumUntil,
    username: u.username || "Player",
    email: u.email || "",
    avatar: u.avatar,
    background: u.background,
    level: levelFromElo(elo),
    elo,
    rank: rankFromElo(elo),
    isOnline: true,
    isPremium: Boolean(u.isPremium),
    isAdmin: !!u.isAdmin,
    createdAt: u.createdAt || new Date().toISOString(),
    stats: mergedStats,
  };
}

/** Прогресс внутри текущего уровня: доля 0..1 и ELO до следующего (null на максимуме). */
export function levelProgress(elo: number): { level: number; ratio: number; toNext: number | null } {
  const level = levelFromElo(elo);
  if (level >= 10) return { level, ratio: 1, toNext: null };
  const from = level === 1 ? 100 : LEVEL_THRESHOLDS[level - 2];
  const to = LEVEL_THRESHOLDS[level - 1];
  return { level, ratio: Math.max(0, Math.min(1, (elo - from) / (to - from))), toNext: to - elo };
}
